import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import AddImage from "../Components/AddImage.jsx";
import EventDateOption from "../Components/EventDateOption.jsx";
import InputContent from "../Components/InputContent.jsx";
import InputTitle from "../Components/InputTitle.jsx";
import RadioButton from "../Components/RadioButton.jsx";
import { ReactComponent as IconInfo } from "../Images/icon_info.svg";
import ImageExEvent from "../Images/imageEx_event.svg";
import ScreenExEvent from "../Images/screenEx_event.svg";
import TabTitleExImage from "../Images/tabTitleEx.png";
import Layout from "../Layout/Layout";
import PaginationForMiniList from "../Layout/PaginationForMiniList.jsx";
import AlarmApply from "../Modals/AlarmApply";
import AlarmCancel from "../Modals/AlarmCancel";
import AlarmInputCheck from "../Modals/AlarmInputCheck";
import ImageEx from "../Modals/ImageEx";
import ImageUpload from "../Modals/ImageUpload";
import TabTitleEx from "../Modals/TabTitleEx.jsx";
import "../Styles/component.css";
import "../Styles/global.css";
import "../Styles/lists.css";
import { postFormData, postJson } from "../Utils/apiUtils.js";
import { useGoToAddr } from "../Utils/navigationUtils.js";
import { showToast } from "../Utils/variables";

const EVENT_TAB_TITLE_MAX_LENGTH = 50;

const formatDateValue = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return String(value).slice(0, 10);
};

const EventApplyContent = () => {
  const goToAddr = useGoToAddr();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditing = location.pathname.includes("/edit/");
  const isDetailMode = location.pathname.includes("/detail/");

  const [noticeId, setNoticeId] = useState(null);
  const [titleOption, setTitleOption] = useState(0);
  const [tabTitle, setTabTitle] = useState("");
  const [image, setImage] = useState(null);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());

  const [inputCheckFail, setInputCheckFail] = useState(false);

  const [clickImageEx, setClickImageEx] = useState(false);
  const [clickImageUpload, setClickImageUpload] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);
  const [clickApply, setClickApply] = useState(false);
  const [clickTitleEx, setClickTitleEx] = useState(false);
  const lastEventDetailRequestKey = useRef(null);

  const applyButtonOK =
    (titleOption === 1 ? tabTitle : true) &&
    image &&
    noticeId &&
    startTime &&
    endTime &&
    title &&
    content;

  const handleCancel = () => {
    goToAddr("event");
  };

  const handleClickX = () => {
    setClickImageEx(false);
    setClickImageUpload(false);
    setInputCheckFail(false);
    setClickCancel(false);
    setClickApply(false);
    setClickTitleEx(false);
  };

  const handleApplyEvent = async () => {
    if (isDetailMode) {
      return;
    }

    if (!applyButtonOK) {
      setInputCheckFail(true);
    } else {
      try {
        const _image = image ? image[0] : null;
        let res;

        const formData = new FormData();
        formData.append("noticeId", noticeId);
        formData.append("title", title);
        formData.append("content", content);
        formData.append("startTime", startTime);
        formData.append("endTime", endTime);
        formData.append("image", _image);
        formData.append("menuType", titleOption + 1);
        formData.append("menuTitle", tabTitle);

        if (isEditing) {
          formData.append("seasonId", id);
          res = await postFormData("/dashboard/event/update", formData, navigate);
        } else {
          res = await postFormData("/dashboard/event/create", formData, navigate);
        }

        if (res.code === 200) {
          navigate("/event", { state: { toast_enable: true } });
          return;
        }

        showToast(res.message ?? "저장 중 오류가 발생했습니다.", "error");
      } catch (error) {
        console.log("이벤트대회 등록 중 에러:", error);
        throw error;
      }
    }
  };

  const getEventData = async () => {
    try {
      const body = { seasonId: id };
      const res = await postJson("/dashboard/event/getDetail", body, navigate);

      if (res.code === 200) {
        const data = res.data;

        setNoticeId(data.noticeId);
        setTitle(data.title);
        setContent(data.content);
        setImage(data.image);
        setStartTime(new Date(data.startTime));
        setEndTime(new Date(data.endTime));
        setTitleOption(data.menuType - 1);
        setTabTitle(data.menuTitle);
      }
    } catch (error) {
      console.log("이벤트대회 상세 가져오기 중 에러");
      throw error;
    }
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    const requestKey = JSON.stringify({ id, isDetailMode });

    if (lastEventDetailRequestKey.current === requestKey) {
      return;
    }

    lastEventDetailRequestKey.current = requestKey;
    getEventData();
  }, [id, isEditing, navigate]);

  const pageTitle = isDetailMode
    ? "이벤트 대회 상세"
    : `이벤트 대회 ${isEditing ? "수정" : "등록"}`;
  const topNoticeText = isDetailMode
    ? "*상세 페이지에서는 대회 정보를 확인할 수 있습니다."
    : "*표시는 필수 입력 항목입니다.";

  return (
    <div>
      {clickTitleEx && (
        <TabTitleEx image={TabTitleExImage} handleClickX={handleClickX} />
      )}
      {clickImageEx && (
        <ImageEx
          title="이벤트 대회 이미지 예시"
          image={
            <img
              src={ImageExEvent}
              alt="이벤트 대회 이미지 예시"
              style={{ width: "100%", display: "block" }}
            />
          }
          screen={
            <img
              src={ScreenExEvent}
              alt="이벤트 대회 앱 적용 화면 예시"
              style={{ width: "100%", display: "block" }}
            />
          }
          handleClickX={handleClickX}
        />
      )}
      {clickImageUpload && (
        <ImageUpload
          title="대회이미지 업로드"
          info="대회 이미지는 1000x339px만 등록 가능합니다"
          handleClickX={handleClickX}
          imageUpload={(value) => setImage(value)}
          pageName="EventApply"
          previewImg={image}
          requiredDimensions={{ width: 1000, height: 339 }}
        />
      )}
      {clickCancel && !isDetailMode && (
        <AlarmCancel
          title="작성하던 내용을 취소하시겠습니까?"
          subtitle="작성하던 내용은 저장되지 않습니다."
          handleCancel={handleCancel}
          handleClickX={handleClickX}
        />
      )}

      {clickApply && !isDetailMode && (
        <AlarmApply
          title={
            isEditing
              ? "이벤트 대회를 수정하시겠습니까?"
              : "이벤트 대회를 등록하시겠습니까?"
          }
          subtitle="등록 시 랭킹마라톤 앱에 데이터가 반영됩니다."
          handleApply={handleApplyEvent}
          handleClickX={handleClickX}
        />
      )}
      {inputCheckFail && <AlarmInputCheck handleClickX={handleClickX} />}
      <EventApplyBox>
        <div className="title-in-box">{pageTitle}</div>
        <div className="hrz-wrapper">
          <div
            className="text-for-notice"
            style={{ marginLeft: "auto", marginTop: "10px" }}
          >
            {topNoticeText}
          </div>
        </div>

        <div style={{ display: "flex" }}>
          <p className="bold-text">이벤트전 탭 타이틀*</p>
          <p
            className="medium-text"
            style={{
              color: "#1167ed",
              cursor: "pointer",
              textDecoration: "underline",
              fontSize: "15px",
              marginLeft: "14px",
            }}
            onClick={() => setClickTitleEx(true)}
          >
            예시
          </p>
        </div>
        <div style={{ height: "30px" }} />
        <RadioButton
          option1="기본"
          option2="직접입력"
          optionChange={(num) => setTitleOption(num)}
          selectedOption={titleOption}
          disabled={isDetailMode}
        />
        {titleOption === 1 && (
          <InputTabTitle
            setTitle={(value) => setTabTitle(value)}
            title={tabTitle}
            readOnly={isDetailMode}
          />
        )}

        <AddImage
          title="대회이미지"
          noticeText="대회 이미지는 1000x339px만 등록 가능합니다"
          handleClickEx={() => setClickImageEx(true)}
          handleClickUpload={() => setClickImageUpload(true)}
          previewImg={image}
          readOnly={isDetailMode}
        />

        <div className="bold-text" style={{ marginTop: "40px" }}>
          공지사항 연결*
        </div>
        <MiniList
          setNoticeId={(id) => setNoticeId(id)}
          noticeId={noticeId}
          readOnly={isDetailMode}
        />

        <div
          className="bold-text"
          style={{ marginTop: "40px", marginBottom: "8px" }}
        >
          옵션 선택
        </div>

        <div className="blue-border-optionBox">
          <div className="hrz-wrapper">
            <p className="medium-text">대회 기간*</p>
            <div style={{ marginLeft: "auto", display: "flex" }}>
              <IconInfo fill="#888888" />
              <p
                style={{
                  fontSize: "15px",
                  color: "#888888",
                  marginLeft: "4px",
                }}
              >
                대회 진행 기간에 맞추어 ‘랭킹{">"}이벤트런’에 활성화
              </p>
            </div>
          </div>

          <div style={{ marginTop: "14px" }}>
            <EventDateOption
              startTitle="시작일"
              endTitle="종료일"
              startDate={startTime}
              endDate={endTime}
              setStartDate={setStartTime}
              setEndDate={setEndTime}
              gap="30px"
              readOnly={isDetailMode}
            />
          </div>
        </div>

        <p className="bold-text" style={{ marginTop: "40px" }}>
          내용 작성*
        </p>
        <InputTitle
          title={title}
          setTitle={setTitle}
          placeholder="제목을 입력해주세요"
          readOnly={isDetailMode}
        />
        <InputContent
          content={content}
          setContent={setContent}
          maxLength={10000}
          readOnly={isDetailMode}
        />

        <div style={{ display: "flex", marginTop: "30px" }}>
          <div
            className="cancelBtn center"
            style={{ marginLeft: "auto" }}
            onClick={() => {
              if (isDetailMode) {
                handleCancel();
                return;
              }

              setClickCancel(true);
            }}
          >
            {isDetailMode ? "목록" : "취소"}
          </div>
          {!isDetailMode && (
            <div
              className="applyBtn center"
              style={{ marginLeft: "16px" }}
              onClick={() => setClickApply(true)}
            >
              {isEditing ? "수정" : "등록"}
            </div>
          )}
        </div>
      </EventApplyBox>
    </div>
  );
};

const InputTabTitle = ({ setTitle, title, readOnly = false }) => {
  const [title_count, setTitle_count] = useState(0);

  const handleTitleChange = (value) => {
    if (readOnly) {
      return;
    }

    if (value?.length <= EVENT_TAB_TITLE_MAX_LENGTH) {
      setTitle(value);
      setTitle_count(value.length);
    }
  };

  useEffect(() => {
    setTitle_count(title?.length ?? 0);
  }, [title]);
  return (
    <div className="textinputBox" style={{ marginTop: "14px" }}>
      <input
        type="text"
        placeholder="타이틀을 입력해주세요"
        style={{ width: "653px", height: "23px" }}
        className="textinput"
        value={title}
        onChange={(e) => handleTitleChange(e.target.value)}
        maxLength={EVENT_TAB_TITLE_MAX_LENGTH}
        readOnly={readOnly}
      />
      <div style={{ color: "#888888", marginLeft: "auto" }}>
        {title_count}/{EVENT_TAB_TITLE_MAX_LENGTH}
      </div>
    </div>
  );
};

const MiniList = ({ setNoticeId, noticeId, readOnly = false }) => {
  const [selectedNoticeId, setSelectedNoticeId] = useState(noticeId);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const offset = 5;
  const lastNoticeListRequestKey = useRef(null);

  const [notice_count, setNotice_count] = useState(0);
  const [notice_list, setNotice_list] = useState([]);

  const getNoticeList = async () => {
    try {
      const body = {
        page,
        offset,
      };
      const res = await postJson("/dashboard/event/getNoticeList", body, navigate);
      if (res.code === 200) {
        setNotice_count(res.data.count);
        setNotice_list(res.data.rows);
      }
    } catch (error) {
      console.log("공지사항리스트(이벤트대회) 가져오기 중 에러");
      throw error;
    }
  };

  useEffect(() => {
    const requestKey = JSON.stringify({ page });

    if (lastNoticeListRequestKey.current === requestKey) {
      return;
    }

    lastNoticeListRequestKey.current = requestKey;
    getNoticeList();
  }, [page]);

  useEffect(() => {
    if (noticeId) {
      setSelectedNoticeId(noticeId);
    }
  }, [noticeId]);

  const handleSelectNotice = (noticeId) => {
    if (readOnly) {
      return;
    }

    setSelectedNoticeId(noticeId);
    setNoticeId(noticeId);
  };

  return (
    <MiniListBox>
      <div style={{ fontSize: "15px", color: "#888888" }}>
        어플 내에서 별주부전 대회 이미지 클릭 시 해당 공지사항 상세로 이동하며,
        공지사항과 연결하여
        <br /> 팝업과 푸시알림을 설정할 수 있습니다.
      </div>
      <div
        className="listArea"
        style={{ width: "700px", height: "324px", marginTop: "16px" }}
      >
        <div
          className="list_titleBar"
          style={{
            width: "700px",
            height: "44px",
            gridTemplateColumns: "80px 100px 520px",
          }}
        >
          <p className="center">NO</p>
          <p className="leftalign">이미지</p>
          <p className="leftalign">제목</p>
        </div>
        {notice_list.map((data) => (
          <div
            key={data.rownum}
            className="list_contentBar"
            style={{
              width: "700px",
              height: "56px",
              gridTemplateColumns: "80px 100px 425px 95px",
              backgroundColor:
                selectedNoticeId === data.noticeId ? "#e4e4e4" : "",
            }}
          >
            <p className="center">{data.rownum}</p>
            <div className="leftalign">
              <div className="emptyImage">
                {data.image ? (
                  <img
                    style={{ width: "100%", height: "100%" }}
                    src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${data.image}`}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
            <p className="leftalign">{data.title}</p>
            <div className="center">
              <div
                className="blueBtn-70x40 center"
                style={{
                  backgroundColor:
                    readOnly
                      ? selectedNoticeId === data.noticeId
                        ? "#1167ed"
                        : "#d9d9d9"
                      : selectedNoticeId === data.noticeId
                      ? "#f25c5c"
                      : "#1167ed",
                  color:
                    readOnly && selectedNoticeId !== data.noticeId
                      ? "#888888"
                      : "#ffffff",
                  cursor: readOnly ? "default" : "pointer",
                  visibility:
                    readOnly && selectedNoticeId !== data.noticeId
                      ? "hidden"
                      : "visible",
                }}
                onClick={() =>
                  !readOnly &&
                  (selectedNoticeId === data.noticeId
                    ? setSelectedNoticeId(null)
                    : handleSelectNotice(data.noticeId))
                }
              >
                {readOnly
                  ? "연결됨"
                  : selectedNoticeId === data.noticeId
                  ? "취소"
                  : "선택"}
              </div>
            </div>
          </div>
        ))}
      </div>

      <PaginationForMiniList
        totalPages={notice_count === 0 ? 1 : Math.ceil(notice_count / 5)}
        currentPage={page}
        onPageChange={setPage}
      />
    </MiniListBox>
  );
};

const MiniListBox = styled.div`
  width: auto;
  height: auto;
  border-radius: 10px;
  padding: 20px;
  background-color: #f5f5f5;
  margin-top: 8px;
`;

const EventApplyBox = styled.div`
  width: auto;
  height: auto;
  border-radius: 20px;
  margin-left: 413px;
  margin-top: 30px;
  margin-bottom: 30px;
  padding: 30px;
  background-color: #ffffff;
  .blue-border-optionBox {
    width: auto;
    height: auto;
    padding: 20px;
    border-radius: 10px;
    box-shadow: inset 0 0 0 1px #c6d9f5;
  }
`;

const EventApply = () => {
  return <Layout page={<EventApplyContent />} />;
};

export default EventApply;
